package com.htmlagent.site.pages

import com.htmlagent.site.components.sections.landing.EnterDialog
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import arrow.core.Either
import com.htmlagent.shared.ApiRoutes
import com.htmlagent.shared.auth.ApprovalPrompt
import com.htmlagent.shared.auth.MeResponse
import com.htmlagent.site.api.AgentClient
import com.htmlagent.site.api.ApiFailure
import com.htmlagent.site.i18n.AppLocale
import com.htmlagent.site.i18n.asMessage
import com.htmlagent.site.state.awaitSignIn
import com.htmlagent.site.state.signedInIdentity
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import com.htmlagent.site.components.sections.ArtifactPanel
import com.htmlagent.site.components.sections.BriefDialog
import com.htmlagent.site.components.sections.ChatPanel
import com.htmlagent.site.components.sections.Gate
import com.htmlagent.site.components.sections.HistoryPanel
import com.htmlagent.site.components.sections.IconRail
import com.htmlagent.site.components.widgets.FadingPage
import com.htmlagent.site.components.widgets.Resizer
import com.htmlagent.site.i18n.LocalStrings
import com.htmlagent.site.state.BackCloses
import com.htmlagent.site.state.BackStack
import com.htmlagent.site.state.Identity
import com.htmlagent.site.state.LayoutPrefs
import com.htmlagent.site.state.WorkspacePane
import com.htmlagent.site.state.rememberNarrowScreen
import com.htmlagent.site.state.chat.ChatPresenter
import com.htmlagent.site.state.brief.BriefPresenter
import com.htmlagent.site.state.chat.ChatUiState
import com.htmlagent.site.theme.AppShellStyle
import com.htmlagent.site.theme.Breakpoints
import com.htmlagent.site.theme.DrawerScrimStyle
import com.htmlagent.site.theme.ThemeTokensStyle
import com.htmlagent.site.theme.EmptyStateStyle
import com.htmlagent.site.theme.WorkspaceStyle
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLMetaElement
import org.w3c.dom.url.URLSearchParams
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Main
import org.jetbrains.compose.web.dom.Text

/** Where `/app` is on the way in: finding out who this is, asking, waiting for the email, or in. */
private sealed interface Entry {
    data object Resolving : Entry
    data class AtGate(val email: String = "", val error: String? = null, val sending: Boolean = false) : Entry

    /** The link is on its way; this window shows [match] and signs in once the link is opened. */
    data class LinkSent(val email: String, val match: Int) : Entry

    /** An access link asked for from another browser: pick that one's number to let it in. */
    data class ApproveLink(val token: String, val prompt: ApprovalPrompt) : Entry

    /** Approved: the other browser is in, and this one is done. */
    data object Approved : Entry
    data class In(val identity: String) : Entry
}

/**
 * The application, at `/app`: who this is first and, once known, the workspace.
 *
 * Who this is comes from the session cookie, which only the server reads. On the way in the page
 * also handles the two links that carry a secret: the emailed access link and a guest's recovery
 * link.
 *
 * The gate stays even though the landing asks for the same address, because `/app` is a link
 * somebody can have bookmarked. It does not block, though: going on as a guest is one tap.
 */
@Page
@Composable
fun AppPage() {
    LaunchedEffect(Unit) { excludeFromSearch() }

    val scope = rememberCoroutineScope()
    val client = remember { AgentClient(baseUrl = ApiBaseUrl) }
    var entry by remember { mutableStateOf<Entry>(Entry.Resolving) }

    fun sendLink(email: String) {
        entry = Entry.AtGate(email, sending = true)
        scope.launch {
            entry = client.requestLink(email, AppLocale.current.value).fold(
                ifLeft = { Entry.AtGate(email, error = it.asMessage()) },
                ifRight = { Entry.LinkSent(email, it.match) },
            )
        }
    }

    fun signedIn(result: Either<ApiFailure, MeResponse>, email: String = "") {
        entry = result.fold(
            ifLeft = { Entry.AtGate(email, error = it.asMessage()) },
            ifRight = { me ->
                // Signed in for real: the identity an older version kept has nothing left to do.
                Identity.forgetLegacy()
                Identity.of(me)?.let(Entry::In) ?: Entry.AtGate(email)
            },
        )
    }

    suspend fun openLink(token: String) {
        client.verify(token).fold(
            ifLeft = { entry = Entry.AtGate(error = it.asMessage()) },
            ifRight = { response ->
                val prompt = response.approve
                val me = response.me
                when {
                    prompt != null -> entry = Entry.ApproveLink(token, prompt)
                    me != null -> signedIn(Either.Right(me))
                    else -> entry = Entry.AtGate()
                }
            },
        )
    }

    fun approveLink(token: String, match: Int) {
        scope.launch {
            client.approve(token, match).fold(
                ifLeft = { entry = Entry.AtGate(error = it.asMessage()) },
                ifRight = { entry = Entry.Approved },
            )
        }
    }

    // Waiting for the link: it signs in here once it is opened in this browser or approved from
    // another. Leaving the state cancels the wait.
    val waiting = entry as? Entry.LinkSent
    LaunchedEffect(waiting) {
        if (waiting == null) return@LaunchedEffect
        signedIn(client.awaitSignIn(), waiting.email)
    }

    // Nobody is signed in, but this browser may have asked for a link before the app was closed:
    // approved meanwhile, it goes in; still waiting, the same screen and number come back. The
    // server keeps both, so closing the waiting window loses nothing.
    suspend fun resumeOrGate() {
        val asked = client.awaitApproval().getOrNull()
        val me = asked?.me
        val waiting = asked?.waiting
        when {
            me != null -> signedIn(Either.Right(me))
            waiting != null -> entry = Entry.LinkSent(waiting.email, waiting.match)
            // An address an older version stored is only a guess at who this is: it fills the
            // field, and the link decides.
            else -> entry = Entry.AtGate(Identity.legacy()?.takeIf(Identity::looksLikeEmail).orEmpty())
        }
    }

    LaunchedEffect(Unit) {
        val secret = takeSecretFromAddress()
        when {
            secret is AddressSecret.AccessLink -> openLink(secret.token)
            secret is AddressSecret.RecoveryKey -> signedIn(client.guest(secret.key))
            else -> client.signedInIdentity().fold(
                ifLeft = { entry = Entry.AtGate(error = it.asMessage()) },
                ifRight = { identity -> if (identity != null) entry = Entry.In(identity) else resumeOrGate() },
            )
        }
    }

    when (val current = entry) {
        // Short, and nothing is painted: a gate flashing before the workspace is worse.
        Entry.Resolving -> Unit

        // The gate lives outside the shell, so it brings its own tokens.
        is Entry.AtGate -> Gate(
            onEnter = ::sendLink,
            onGuest = { scope.launch { signedIn(client.guest()) } },
            initialEmail = current.email,
            serverError = current.error,
            sending = current.sending,
        )

        is Entry.LinkSent -> Gate(
            onEnter = ::sendLink,
            onGuest = { scope.launch { signedIn(client.guest()) } },
            sentTo = current.email,
            match = current.match,
            onOtherEmail = { entry = Entry.AtGate() },
        )

        is Entry.ApproveLink -> Gate(
            onEnter = ::sendLink,
            onGuest = { scope.launch { signedIn(client.guest()) } },
            approve = current.prompt,
            onApprove = { match -> approveLink(current.token, match) },
            onOtherEmail = { entry = Entry.AtGate() },
        )

        Entry.Approved -> Gate(
            onEnter = ::sendLink,
            onGuest = { scope.launch { signedIn(client.guest()) } },
            approved = true,
        )

        is Entry.In -> Workspace(client, current.identity, onSignedIn = { signedIn(Either.Right(it)) })
    }
}

/**
 * Keeps the workspace out of the search index.
 *
 * The `head` Kobweb writes is one for the whole site, so the static export hands `/app` the
 * landing's title and description: two indexable pages with the same metadata, and this one
 * showing nothing at all without JavaScript. A `Disallow` in `robots.txt` only asks a crawler
 * not to fetch the URL, which does not stop it being listed; the meta is what does. The export
 * snapshots the rendered DOM, so writing it from here is what puts it into `app.html`.
 */
private fun excludeFromSearch() {
    val head = document.head ?: return
    document.title = "HAL"

    // The site-wide head declares `index, follow`. It is overwritten and not appended to:
    // two `robots` metas saying opposite things is an ambiguity a crawler resolves on its
    // own terms, and the point here is to leave it no choice.
    val robots = head.querySelector("meta[name=\"robots\"]") as? HTMLMetaElement
        ?: (document.createElement("meta") as HTMLMetaElement).also {
            it.name = "robots"
            head.appendChild(it)
        }
    robots.content = "noindex, follow"

    // The canonical is the landing's, and a `noindex` page pointing its canonical at another
    // page asks the crawler to obey two rules that contradict each other. It goes.
    head.querySelector("link[rel=\"canonical\"]")?.remove()
}

/** A secret the address arrived with: the emailed access link's token, or a guest's recovery key. */
private sealed interface AddressSecret {
    data class AccessLink(val token: String) : AddressSecret
    data class RecoveryKey(val key: String) : AddressSecret
}

/**
 * The secret in the address, if any, taken out of it at once: it is a credential, and leaving it
 * in the bar invites it into a screenshot, a shared link or the next `Referer`. `replaceState`
 * and not `pushState`, so Back does not walk into the link again.
 */
private fun takeSecretFromAddress(): AddressSecret? {
    val params = URLSearchParams(window.location.search)
    val token = params.get(ApiRoutes.Auth.LINK_TOKEN_PARAM)?.trim()?.takeIf { it.isNotBlank() }
    val key = params.get(Identity.RECOVERY_PARAM)?.trim()?.takeIf { it.isNotBlank() }
    val secret = token?.let(AddressSecret::AccessLink) ?: key?.let(AddressSecret::RecoveryKey) ?: return null
    window.history.replaceState(null, "", window.location.pathname)
    return secret
}

/**
 * Chat on the left, artifact on the right, rail and history at the edge.
 *
 * Only which of the three screens we are on is collected here; the messages and the artifact are
 * collected by each panel, so a text token does not recompose the rail or the history. The theme
 * tokens are pinned on the outermost element and everything inside inherits them.
 *
 * The chat width travels as a custom property: changing it repaints the grid without remounting
 * the panels, so the iframe is not reloaded while dragging. It goes through `styleModifier` and
 * not `attr("style", …)`, which is written once on mount and not reapplied on recomposition.
 *
 * Below [Breakpoints.Md] there is no room for the two side by side, and stacking them gave each
 * half a screen that was too little for either. So one pane is on at a time and the other is
 * hidden rather than unmounted: crossing over keeps the conversation's scroll, the half-typed
 * message and the page the iframe already loaded. It is a pane and not a route for the same
 * reason - a route change would rebuild the presenter and drop the stream with it.
 */
@Composable
private fun Workspace(client: AgentClient, identity: String, onSignedIn: (MeResponse) -> Unit) {
    val t = LocalStrings.current
    val scope = rememberCoroutineScope()

    val presenter = remember(identity) { ChatPresenter(client, scope, ArtifactBaseUrl, ApiBaseUrl) }

    // The assistant only ever produces text, so it needs nothing but somewhere to send it.
    val brief = remember { BriefPresenter() }

    // Read once: the address is cleaned as soon as it is read, so a reload does not start over.
    val shortcut = remember { consumeShortcut() }
    LaunchedEffect(presenter) { startSession(presenter, brief, shortcut) }
    DisposableEffect(presenter) { onDispose { presenter.dispose() } }

    val current by presenter.state.collectAsState()
    val sessions by presenter.sessions.collectAsState()
    val openSession by presenter.currentSessionId.collectAsState()
    val guestLimitReached by presenter.guestLimitReached.collectAsState()
    var claim by remember { mutableStateOf(EntryDialog()) }
    var showHistory by remember { mutableStateOf(false) }
    var chatWidth by remember { mutableStateOf(LayoutPrefs.chatWidth()) }

    val narrow = rememberNarrowScreen()
    var pane by remember { mutableStateOf(WorkspacePane.CHAT) }

    val drawer = narrow && showHistory
    // The ≡ of the phone's app bar: the rail is not there to open the drawer.
    val openMenu = onPhone(narrow) { showHistory = true }

    PhoneLayers(
        drawer = drawer,
        page = narrow && pane == WorkspacePane.ARTIFACT,
        onDrawerClosed = { showHistory = false },
        onPageClosed = { pane = WorkspacePane.CHAT },
    )

    fun closeHistory() = backOr(open = drawer) { showHistory = false }

    // Opening or starting a session from the drawer leaves both the drawer and the page behind.
    fun leaveLayers() = popAllOr(phone = narrow) { showHistory = false }

    // Opening another session lands on the conversation, whatever was showing before: the page of
    // the session just left is not this one's, and a session that has produced nothing has no page.
    LaunchedEffect(openSession) { pane = WorkspacePane.CHAT }

    FadingPage(AppShellStyle.toModifier().then(ThemeTokensStyle.toModifier())) { leave ->
        IconRail(
            historyOpen = showHistory,
            onNewSession = {
                brief.reset()
                presenter.newSession()
            },
            onNewGuidedSession = {
                brief.reset()
                presenter.newSession()
                brief.show()
            },
            onToggleHistory = { showHistory = !showHistory },
            onSwitchUser = {
                // Back to the landing, which is where the next address is typed. The gate at
                // `/app` would do too, but leaving is a decision about who you are, and the
                // page that asks that question is the public one.
                signOut(client, scope) { leave("/") }
            },
        )

        // Always mounted: an element that appears out of nothing has nowhere to animate from.
        HistoryPanel(
            open = showHistory,
            sessions = sessions,
            currentId = openSession,
            identity = identity,
            onOpen = { id ->
                presenter.open(id)
                // The panel lies over the conversation, and the session just picked is behind it.
                leaveLayers()
            },
            onDelete = presenter::delete,
            onCollapse = { closeHistory() },
            onNewSession = {
                leaveLayers()
                brief.reset()
                presenter.newSession()
            },
            onNewGuidedSession = {
                leaveLayers()
                brief.reset()
                presenter.newSession()
                brief.show()
            },
            onSwitchUser = { signOut(client, scope) { leave("/") } },
            onClaim = { email ->
                // Nothing moves yet: the pages go to the address once its link is opened here or
                // approved from another browser, because the guest's cookie travels with that
                // sign-in. The wait, with its number, is the dialog's, as when turns run out.
                client.requestLink(email, AppLocale.current.value).fold(
                    ifLeft = { it.asMessage() },
                    ifRight = {
                        claim = EntryDialog(sentTo = email, match = it.match)
                        null
                    },
                )
            },
        )

        // Out of guest turns: the way on is an address, and the pages move to it once its link
        // is opened here or approved from another browser. The claim in the sessions panel ends
        // here too, once its link is on its way.
        EnterDialog(
            visible = guestLimitReached || claim.sentTo != null,
            onEnter = { email ->
                claim = EntryDialog(sending = true)
                scope.launch {
                    claim = client.requestLink(email, AppLocale.current.value).fold(
                        ifLeft = { EntryDialog(error = it.asMessage()) },
                        ifRight = { EntryDialog(sentTo = email, match = it.match) },
                    )
                }
            },
            onGuest = null,
            onClose = {
                presenter.dismissGuestLimit()
                claim = EntryDialog()
            },
            sending = claim.sending,
            error = claim.error,
            sentTo = claim.sentTo,
            match = claim.match,
            onOtherEmail = { claim = EntryDialog() },
            title = if (guestLimitReached) t.gate.guestLimitTitle else t.history.identityGuestTitle,
            lead = if (guestLimitReached) t.gate.guestLimitLead else t.history.identityGuestBody,
        )

        // Waiting for the link asked for in that dialog; once in, the workspace reopens as the
        // address, with the guest's pages already moved to it.
        val claimMatch = claim.match
        LaunchedEffect(claimMatch) {
            if (claimMatch == null) return@LaunchedEffect
            client.awaitSignIn().fold(
                ifLeft = { claim = EntryDialog(error = it.asMessage()) },
                ifRight = { me ->
                    presenter.dismissGuestLimit()
                    claim = EntryDialog()
                    onSignedIn(me)
                },
            )
        }

        // What dims the app behind the sessions panel; touching it closes the panel.
        Div(
            DrawerScrimStyle.toModifier()
                .onClick { closeHistory() }
                .toAttrs { attr("data-open", showHistory.toString()) },
        )

        Main(
            WorkspaceStyle.toModifier()
                .then(Modifier.styleModifier { property("--chat-width", "${chatWidth}px") })
                .toAttrs(),
        ) {
            when (val ui = current) {
                ChatUiState.Connecting -> Div(EmptyStateStyle.toModifier().toAttrs()) {
                    Text(t.landing.openingSession)
                }

                is ChatUiState.Failed -> Div(EmptyStateStyle.toModifier().toAttrs()) {
                    Text(ui.message)
                }

                ChatUiState.Ready -> {
                    ChatPanel(
                        presenter = presenter,
                        onSend = presenter::send,
                        onRetry = presenter::retry,
                        onCancel = presenter::cancel,
                        onSharpen = brief::show,
                        off = narrow && pane != WorkspacePane.CHAT,
                        onOpenArtifact = onPhone(narrow) { pane = WorkspacePane.ARTIFACT },
                        onOpenMenu = openMenu,
                    )
                    Resizer(current = chatWidth) { width ->
                        chatWidth = width
                        LayoutPrefs.rememberChatWidth(width)
                    }
                    ArtifactPanel(
                        presenter = presenter,
                        onSelectVersion = presenter::showVersion,
                        off = narrow && pane != WorkspacePane.ARTIFACT,
                        // Spends the step the page took, so the back gesture has nothing left to undo.
                        onBackToChat = onPhone(narrow) { window.history.back() },
                        onOpenMenu = openMenu,
                    )
                    BriefDialog(presenter = brief, onGenerate = presenter::send)
                }
            }
        }
    }
}

/** The installed app's shortcuts (`shortcuts` in the manifest) open `/app` with one of these. */
private const val SHORTCUT_PARAM = "nuevo"
private const val SHORTCUT_BLANK = "blanco"
private const val SHORTCUT_GUIDED = "asistente"

/**
 * The shortcut the app was opened from, if any, taken out of the address.
 *
 * `replaceState` and not `pushState`, so Back does not return to the shortcut's address.
 */
private fun consumeShortcut(): String? {
    val params = URLSearchParams(window.location.search)
    val shortcut = params.get(SHORTCUT_PARAM)?.takeIf { it == SHORTCUT_BLANK || it == SHORTCUT_GUIDED }
    if (shortcut != null) window.history.replaceState(null, "", window.location.pathname)
    return shortcut
}

/**
 * Starts the workspace. Opened from a shortcut it begins a new session instead of the last one,
 * and with the assistant open when that is the shortcut.
 */
private fun startSession(presenter: ChatPresenter, brief: BriefPresenter, shortcut: String?) {
    if (shortcut != null) Identity.forgetSession()
    presenter.start()
    if (shortcut == SHORTCUT_GUIDED) brief.show()
}

/**
 * Closes the session on the server and forgets this browser's conveniences, then [then]. The
 * server call failing does not keep anyone signed in: the cookie is cleared by the response, and
 * if there was none, there was nothing to close.
 */
private fun signOut(client: AgentClient, scope: CoroutineScope, then: () -> Unit) {
    scope.launch {
        client.logout()
        Identity.forget()
        then()
    }
}

/**
 * On a phone the drawer and the page pane are both layers the back gesture closes; see
 * [BackStack]. A reload keeps the history entry, so a depth left over from before is cleared.
 */
@Composable
private fun PhoneLayers(drawer: Boolean, page: Boolean, onDrawerClosed: () -> Unit, onPageClosed: () -> Unit) {
    LaunchedEffect(Unit) { if (BackStack.depth() > 0) window.history.replaceState(null, "") }
    BackCloses(open = drawer, onBack = onDrawerClosed)
    BackCloses(open = page, onBack = onPageClosed)
}

/** An action that only exists on a phone, where the panes and the drawer take turns. */
private fun onPhone(narrow: Boolean, action: () -> Unit): (() -> Unit)? = if (narrow) action else null

/** Closes an open layer by spending its history step, or runs [otherwise] when there is none. */
private fun backOr(open: Boolean, otherwise: () -> Unit) {
    if (open) window.history.back() else otherwise()
}

/** Closes every layer at once on a phone, or runs [otherwise] where there are none. */
private fun popAllOr(phone: Boolean, otherwise: () -> Unit) {
    if (phone) BackStack.popAll() else otherwise()
}

/**
 * API base, resolved at runtime from the host: the site is exported static and there is no
 * per-environment build in which to inject a variable.
 */
internal val ApiBaseUrl: String
    get() = if (isDevelopment) "http://localhost:8080" else ""

/** Artifact origin. It stays separate in production too: it is the generated HTML's isolation. */
private val ArtifactBaseUrl: String
    get() = if (isDevelopment) "http://localhost:8082" else "https://artifacts.${kotlinx.browser.window.location.host}"

internal val isDevelopment: Boolean
    get() = kotlinx.browser.window.location.hostname.let { it == "localhost" || it == "127.0.0.1" }
